/**
 * save-queue.ts
 *
 * Pure save-retry logic — no React, fully testable.
 * Drives the ResourcesProvider's background save pipeline.
 */

export type SaveStatus = 'pending' | 'saving' | 'saved' | 'failed' | 'retrying';

export interface SaveResult {
  finalStatus: SaveStatus;
  /** True when the server returned 401 — the teacher's session has expired. */
  authExpired: boolean;
}

const BACKOFF_MS: readonly number[] = [1000, 2000, 4000];
const DEFAULT_MAX_RETRIES = 3;

/** Returns true for HTTP status codes that are worth retrying automatically. */
export function isTransientError(httpStatus: number): boolean {
  return httpStatus === 0 || httpStatus === 429 || httpStatus >= 500;
}

/**
 * Executes one fetch attempt and normalises network throws into status 0.
 * Never throws; always resolves.
 */
export async function attemptFetch(
  fetchFn: () => Promise<Response>,
): Promise<{ ok: boolean; status: number; authExpired: boolean }> {
  try {
    const res = await fetchFn();
    return { ok: res.ok, status: res.status, authExpired: res.status === 401 };
  } catch {
    // Network failure, DNS error, CORS abort, etc.
    return { ok: false, status: 0, authExpired: false };
  }
}

/**
 * Attempts a save up to `maxRetries` times with exponential backoff.
 *
 * @param fetchFn   Factory that creates a **fresh** Request on each attempt.
 *                  Must be pure: called multiple times without side-effects.
 * @param onStatus  Called whenever the status transitions.
 * @param maxRetries Total additional attempts after the first (default 3 → 4 total).
 * @param _delayFn  Injectable for deterministic testing; defaults to real setTimeout.
 */
export async function saveWithRetry(
  fetchFn: () => Promise<Response>,
  onStatus: (s: SaveStatus, attempt: number) => void,
  maxRetries = DEFAULT_MAX_RETRIES,
  _delayFn: (ms: number) => Promise<void> = (ms) =>
    new Promise((r) => setTimeout(r, ms)),
): Promise<SaveResult> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (attempt === 0) {
      onStatus('saving', attempt);
    } else {
      onStatus('retrying', attempt);
      await _delayFn(
        BACKOFF_MS[Math.min(attempt - 1, BACKOFF_MS.length - 1)],
      );
      onStatus('saving', attempt);
    }

    const { ok, status, authExpired } = await attemptFetch(fetchFn);

    if (ok) {
      onStatus('saved', attempt);
      return { finalStatus: 'saved', authExpired: false };
    }

    // Auth failure — do NOT retry; signal the caller to preserve the payload
    if (authExpired) {
      onStatus('failed', attempt);
      return { finalStatus: 'failed', authExpired: true };
    }

    // Non-retryable client errors (400, 403, 404, 409, …) — fail immediately
    if (!isTransientError(status)) {
      onStatus('failed', attempt);
      return { finalStatus: 'failed', authExpired: false };
    }

    // Transient error (0, 429, 5xx) — retry if attempts remain
    if (attempt === maxRetries) {
      onStatus('failed', attempt);
      return { finalStatus: 'failed', authExpired: false };
    }
  }

  onStatus('failed', 0);
  return { finalStatus: 'failed', authExpired: false };
}
